import React, {useState, useEffect} from "react";
import PropTypes from "prop-types";
import {
  Stepper,
  makeStyles,
  StepLabel,
  StepContent,
  Button,
  Step,
  Typography, FormHelperText, Grid
} from "@material-ui/core";
import DummyContent from "../UI/DummyContent/DummyContent";
import EmailVerification from "../EmailVerification/EmailVerification";
import {compose} from "../../utils/compose";
import {connect} from "react-redux";
import BillingInformationForm from "../BillingInformation/BillingInformationForm";
import SetupTrialPlan from "../Plans/SetupTrialPlan";
import {userInfoBaseUrl, httpClient, subscriptionBaseUrl} from "../../http-client/http-client";
import {accountVerificationActions} from "../../store/actions/accountVerification/accountVerification";
import {useTranslation} from "react-i18next";
import {logger} from '../../logging/logger';
import BillingSetupWizard from "./BillingSetupWizard";
import {useHistory} from 'react-router-dom';
import {select} from "@redux-saga/core/effects";
import InlineSpinner from "../UI/InlineSpinner/InlineSpinner";

const log = logger('AccountSetup');

const useStyles = makeStyles(theme => ({
  root: {},
  actionsContainer: {
    marginTop: theme.spacing(2),
    display: "flex",
    alignContent: "center",
    justifyContent: "center"
  },
  button: {
    marginLeft: theme.spacing(1)
  },
  skipButton: {
    marginLeft: theme.spacing(1),
    textTransform: "none"
  },
  outlined: {
    border: "1px solid rgba(0,0,0,0.22)",
    borderRadius: "5px",
    padding: theme.spacing(1)
  },
  content: {
    padding: theme.spacing(1)
  }
}));

const AccountSetup = props => {
  const classes = useStyles();
  const {emailVerified, subscriptionStatus, onRefreshVerificationStatus, access_token} = props;
  const [stepCompleted, setStepCompleted] = useState([
    emailVerified,
    false,
    false
  ]);
  const firstNonCompletedStep = stepCompleted.indexOf(false);
  const [settingUp, setSettingUp] = useState(false);
  const [activeStep, setActiveStep] = useState(firstNonCompletedStep);
  const [trialStarted, setTrialStarted] = useState(subscriptionStatus.Exists);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const {t} = useTranslation();
  const [setupStepCompleted, setSetupStepCompleted] = useState([
    false,
    false
  ]);
  const [setupActiveStep, setSetupActiveStep] = useState(0);
  const [setupError, setSetupError] = useState(null);

  function handleNext() {
    setActiveStep(prevActiveStep => prevActiveStep + 1);
  }

  function handleBack() {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  }

  function goToPlanSelection() {
    setActiveStep(1);
    setSelectedPlan(null);
  }

  function markStep(index, value) {
    setStepCompleted(prev =>
      prev.map((e, i) => {
        if (i === index) {
          return value;
        } else {
          return e;
        }
      })
    );
  }

  useEffect(() => {
    markStep(0, emailVerified);
  }, [emailVerified]);

  const planSelectedHandler = plan => {
    log.debug(`Selected plan ${plan.Id}`);
    markStep(1, true);

    setSelectedPlan(plan);
    handleNext();
  };

  function startTrial() {
    setSettingUp(true);
    setSetupError(null);

    const url = `${userInfoBaseUrl}/setup/${selectedPlan.Id}`;

    httpClient.post(url, access_token).then(
      ok => {
        log.debug("account setup done");
        setSettingUp(false);
        setTrialStarted(true);
        refreshVerificationStatus();
      },
      error => {
        setSettingUp(false);
        setSetupError(error.error.HumanError);
        log.debug(error);
      }
    );
  }

  async function startSubscription(payload) {
    setSettingUp(true);
    setSetupError(null);
    const url = `${subscriptionBaseUrl}/createOrUpgradeWithPayment`;

    try {
      const response = await httpClient.post(url, access_token, {
        Payload: payload,
        PlanId: selectedPlan.Id
      });
      setSettingUp(false);
      setTrialStarted(true);
      refreshVerificationStatus();
    } catch (error) {
      if(error.error && error.error.HumanError.startsWith("payment_processing_failed")) {
        setSetupError(error.error.HumanError.split(':')[1]);
      } else if(error.error) {
        setSetupError(error.error.HumanError);
      } else {
        setSetupError(error);
      }
      setSettingUp(false);
      log.error(error);
    }
  }

  function refreshVerificationStatus() {
    onRefreshVerificationStatus();
  }

  async function onWizardCompleted(payload) {
    await startSubscription(payload)
  }

  return (
    <div className={classes.root}>
      <Stepper activeStep={activeStep} orientation="vertical" elevation={1}>
        <Step>
          <StepLabel>{t('verifyEmail')}</StepLabel>
          <StepContent>
            <EmailVerification/>
            <div className={classes.actionsContainer}>
              <div>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                  className={classes.button}
                  disabled={!stepCompleted[0]}
                >
                  {t('next')}
                </Button>
              </div>
            </div>
          </StepContent>
        </Step>
        <Step>
          <StepLabel>{t('selectPlan')}</StepLabel>
          <StepContent>
            {/*<SetupTrialPlan onPlanSelected={planSelectedHandler}/>*/}
            {trialStarted ? (
              <>
                <Typography variant="body1" paragraph>
                  {t('trialStarted')}
                </Typography>
                <Typography variant="body1" paragraph>
                  {t('returnToSetupBillingInfo')}
                </Typography>
                <Button variant="contained" color="primary"
                        onClick={refreshVerificationStatus}>{t('continueLabel')}</Button>
              </>
            ) : (
              <SetupTrialPlan
                onPlanSelected={planSelectedHandler}
                submitting={settingUp}
              />
            )}
          </StepContent>
        </Step>
        <Step>
          <StepLabel>{t('setupAccount')}</StepLabel>
          <StepContent>
            {selectedPlan !== null && !settingUp ? (
              <>
                <div className={classes.content}>
                  <BillingSetupWizard plan={selectedPlan}
                                      onCompleted={(paymentPayload) => onWizardCompleted(paymentPayload)}/>
                </div>
                {setupError && (
                  <Grid container direction="row" alignItems="center" justify="center">
                    <Grid item>
                      <FormHelperText className="Mui-error">{setupError}</FormHelperText>
                    </Grid>
                  </Grid>
                )}


                <div className={classes.actionsContainer}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={goToPlanSelection}
                    className={classes.button}
                  >
                    {t('selectPlan')}
                  </Button>
                  {selectedPlan.TrialPeriod && (
                    <Button variant="text" color="primary" onClick={startTrial} className={classes.skipButton}>{t('skipSetupAccount')}</Button>
                  )}
                </div>
              </>
            ) : null}
            {settingUp && (
              <InlineSpinner size="small" align="center" />
            )}
          </StepContent>
        </Step>
      </Stepper>
    </div>
  );
};

AccountSetup.propTypes = {};

const mapStateToProps = state => {
  return {
    access_token: state.oidc.user.access_token,
    emailVerified:
    state.accountVerification.verificationStatus.emailVerificationStatus
      .IsVerified,
    subscriptionStatus:
    state.accountVerification.verificationStatus.subscriptionStatus
  };
};

const mapDispatchToProps = dispatch => ({
  onRefreshVerificationStatus: () => dispatch(accountVerificationActions.fetchStatus())
});

const composed = compose(connect(mapStateToProps, mapDispatchToProps))(AccountSetup);

export default composed;
